import { defineStore } from 'pinia'
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import type { MDBVegcoCommandeVente } from '@/types/vegco/commandevente'
import { emptyMongoId } from '@/utils/helpers'

declare interface VegcoStore {
  inventoryLoaded: boolean
  datesLoaded: number[]
  bucketsStore: MDBVegcoBucket[]
  commandeInventaire: MDBVegcoCommandeVente | null
  commandesStore: MDBVegcoCommandeVente[]
  dernièreCultureSélectionnée: string
  révision: string
}

export const useVegcoStore = defineStore('vegco', {
  persist: false,
  state: () => {
    return {
      inventoryLoaded: false,
      datesLoaded: [] as number[],
      bucketsStore: [] as MDBVegcoBucket[],
      commandeInventaire: null,
      commandesStore: [] as MDBVegcoCommandeVente[],
      dernièreCultureSélectionnée: '',
      révision: ''
    } as VegcoStore
  },
  actions: {
    clear() {
      this.inventoryLoaded = false
      this.datesLoaded = []
      this.bucketsStore = []
      this.commandeInventaire = null
      this.commandesStore = []
      this.dernièreCultureSélectionnée = ''
      this.révision = ''
    },

    async isRevisionChanged() {
      const result = await apiProphet.get(`sites/VGCO/revisions/buckets_commandes`)
      return this.révision != result.data
    },

    async _verifyRevisions() {
      const result = await apiProphet.get(`sites/VGCO/revisions/buckets_commandes`)
      if (this.révision != result.data) {
        const culture = this.dernièreCultureSélectionnée
        this.clear()
        this.révision = result.data
        this.dernièreCultureSélectionnée = culture
      }
    },

    async _verifyCulture(culture: string) {
      if (this.dernièreCultureSélectionnée != culture) {
        const revision = this.révision
        this.clear()
        this.révision = revision
        this.dernièreCultureSélectionnée = culture
      }
    },

    async loadDay(date: Date) {
      const storeU = useUtilisateurStore()
      const culture = storeU.cultureSélectionnée.nom
      await this._verifyRevisions()
      await this._verifyCulture(culture)
      await this._loadInventory(culture)
      await this._loadDaysIfNecessary(date, date, culture)
    },

    async loadDays(dateMin: Date, dateMax: Date) {
      const storeU = useUtilisateurStore()
      const culture = storeU.cultureSélectionnée.nom
      await this._verifyRevisions()
      await this._verifyCulture(culture)
      await this._loadInventory(culture)
      await this._loadDaysIfNecessary(dateMin, dateMax, culture)
    },

    async loadDaysMultipleCultures(dateMin: Date, dateMax: Date, cultures: string[]) {
      this.révision = ''
      await this._verifyRevisions()
      this.dernièreCultureSélectionnée = ''
      for (const culture of cultures) {
        await this._loadInventory(culture)
        await this._loadDaysIfNecessary(dateMin, dateMax, culture)
        this.inventoryLoaded = false
        this.datesLoaded = []
      }
    },

    getStoredBuckets(day: Date) {
      const dateMin = new Date(day.getFullYear(), day.getMonth(), day.getDate())
      const dateMax = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999)
      return this.bucketsStore.filter((b) => {
        const d = new Date(b.date)
        return d >= dateMin && d <= dateMax
      })
    },

    getStoredBucketsRange(minDay: Date, maxDay: Date) {
      const dateMin = new Date(minDay.getFullYear(), minDay.getMonth(), minDay.getDate())
      const dateMax = new Date(maxDay.getFullYear(), maxDay.getMonth(), maxDay.getDate(), 23, 59, 59, 999)
      return this.bucketsStore.filter((b) => {
        const d = new Date(b.date)
        return d >= dateMin && d <= dateMax
      })
    },

    async _loadInventory(culture: string) {
      if (!this.inventoryLoaded) {
        const result = await apiProphet.get(`vgco/commandes/vente/inventaire`)
        this.commandeInventaire = result.data as MDBVegcoCommandeVente

        const config = { params: { culture: culture } }
        const result3 = await apiProphet.get(`vgco/buckets/inventaire`, config)
        await this._addNewBucketsToStoreAsync(result3.data as MDBVegcoBucket[])
        this.inventoryLoaded = true
      }
    },

    async _loadDaysIfNecessary(dateStart: Date, dateEnd: Date, culture: string) {
      if (culture == '') {
        return
      }

      let dateMin = new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate())
      let dateMax = new Date(dateEnd.getFullYear(), dateEnd.getMonth(), dateEnd.getDate(), 23, 59, 59, 999)

      const datesTodo = [] as Date[]
      for (const day = dateMin; day <= dateMax; day.setDate(day.getDate() + 1)) {
        const dd = new Date(day)
        const d = dd.getTime()
        if (!this.datesLoaded.includes(d)) {
          datesTodo.push(dd)
        }
      }

      if (datesTodo.length == 0) {
        return
      }

      dateMin = datesTodo[0]
      dateMax = new Date(datesTodo[datesTodo.length - 1].getTime())
      dateMax.setHours(23, 59, 59, 999)

      const config = { params: { culture: culture, dateMin: dateMin, dateMax: dateMax } }

      // Get buckets
      const result = await apiProphet.get(`vgco/sites/VGCO/buckets`, config)
      await this._addNewBucketsToStoreAsync(result.data as MDBVegcoBucket[])

      // Get inventory command buckets
      if (this.commandeInventaire != null) {
        const result2 = await apiProphet.get(`vgco/commandes/vente/${this.commandeInventaire.id}/buckets`, config)
        await this._addNewBucketsToStoreAsync(result2.data as MDBVegcoBucket[])
      }

      // Get commandes
      const result3 = await apiProphet.get(`vgco/commandes/vente`, config)
      await this._addNewCommandesToStore(result3.data as MDBVegcoCommandeVente[])

      // Push missing days
      for (const day = dateMin; day <= dateMax; day.setDate(day.getDate() + 1)) {
        const dd = new Date(day)
        const d = dd.getTime()
        if (!this.datesLoaded.includes(d)) {
          this.datesLoaded.push(d)
        }
      }
    },

    async _addNewCommandesToStore(commandes: MDBVegcoCommandeVente[]) {
      const newCommandes = commandes.filter((c) => { return !this.commandesStore.some((c2) => c2.id === c.id) })
      this.commandesStore.push(...newCommandes)

      // Get buckets for new commandes
      const newCommandeIds = newCommandes.map((c) => c.id)
      const result = await apiProphet.post(`vgco/commandes/vente/multiple/buckets`, newCommandeIds)
      await this._addNewBucketsToStoreAsync(result.data as MDBVegcoBucket[])
    },

    async _addNewBucketsToStoreAsync(buckets: MDBVegcoBucket[]) {
      const storeU = useUtilisateurStore()
      const newBuckets = buckets.filter((b) => !this.bucketsStore.some((b2) => b.id === b2.id))
      for (const bucket of newBuckets) {
        await storeU.getOrFetchSiteLettresDappel(bucket.siteId)
      }
      this.bucketsStore.push(...newBuckets)
    },

    getResteÀAllouer(commande: MDBVegcoCommandeVente, codeProduit: string) {
      const détail = commande.détails.find((d) => d.codeProduit == codeProduit)
      if (détail == undefined) {
        return 0
      }

      const buckets = this.bucketsStore.filter((b) =>
        b.commandeVenteId == commande.id && b.codeProduit == codeProduit)
      let reste = détail.quantité
      for (const bucket of buckets) {
        reste -= bucket.quantité
      }
      return reste
    },

    getSiteLettresDappelRenommé(siteId: string | undefined) {
      if (siteId === undefined || siteId === '') {
        return undefined
      }

      const storeU = useUtilisateurStore()
      const lda = storeU.getSiteLettresDappel(siteId)
      if (lda == 'FHVW') {
        return 'HOTTE'
      }
      return lda
    },

    getSiteCouleur(siteLettreDappel: string | undefined) {
      switch (siteLettreDappel) {
        case 'DLFL': return '#4992FF';
        case 'JPLG': return '#5bc487ff';
        case 'FHVW': return '#FDDD60';
        case 'HOTTE': return '#FDDD60';
        case 'PHVW': return '#FF6E76';
        case 'VGCO': return '#FF9020';
      }
      return '#AAAAAA';
    },

    getQuantitéPrévision(codeProduit: string, qtéParPalette: number, currentDate: Date) {
      let qté = 0
      if (this.commandeInventaire != null) {
        // Up to today midnight
        //const dateMorning = new Date(currentDate.getFullYear(),
          //currentDate.getMonth(), currentDate.getDate())
        const dateMidnight = new Date(currentDate.getFullYear(),
          currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999)

        // Add pour inventaire not received yet of past days
        this.bucketsStore.forEach((b) => {
          if (b.commandeVenteId == this.commandeInventaire?.id &&
            b.quantitéParPalette == qtéParPalette &&
            b.codeProduit == codeProduit &&
            new Date(b.date) <= dateMidnight) {
            qté += b.quantité - b.quantitéConfirmée
          }
        })

        // Remove buckets previsions
        this.bucketsStore.forEach((b) => {
          if (b.estPrévision && b.codeProduit == codeProduit &&
            b.quantitéParPalette == qtéParPalette &&
            b.commandeVenteId != emptyMongoId() && new Date(b.date) <= dateMidnight) {
            qté -= b.quantité - b.quantitéConfirmée
          }
        })
      }

      return qté
    }
  }
})

